import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate } from "@/shared/lib/date-time";
import { AreaChart, type AreaChartData } from "@/shared/ui/AreaChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import {
  BarChart3Icon,
  DownloadIcon,
  GraduationCapIcon,
  LogInIcon,
  TrendingUpIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import {
  useAttendanceReportSummaryQuery,
  useAttendanceReportTrendQuery,
} from "../api/queries";
import {
  AttendanceReportFilter,
  type AttendanceReportSearch,
} from "../ui/AttendanceReportFilter";
import { AttendanceReportTable } from "../ui/AttendanceReportTable";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "../ui/ReportSummaryStatCard";

function useReportQuery(search: AttendanceReportSearch) {
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const dateFrom = search.dateFrom ?? defaultFrom;
  const dateTo = search.dateTo ?? defaultTo;

  const summaryQuery = useMemo(
    () => ({
      dateFrom,
      dateTo,
      classTypeId: search.classTypeId,
      levelId: search.levelId,
      instructorId: search.instructorId,
    }),
    [dateFrom, dateTo, search.classTypeId, search.levelId, search.instructorId],
  );

  const trendQuery = useMemo(
    () => ({
      ...summaryQuery,
      groupBy: "day" as const,
    }),
    [summaryQuery],
  );

  return { dateFrom, dateTo, summaryQuery, trendQuery };
}

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function AttendanceReportPage() {
  const search = useSearch({
    from: "/app/reports/attendance",
  }) as AttendanceReportSearch;

  const { dateFrom, dateTo, summaryQuery, trendQuery } = useReportQuery(search);

  const { data: summary, isLoading: summaryLoading } =
    useAttendanceReportSummaryQuery(summaryQuery);
  const { data: trend = [], isLoading: trendLoading } =
    useAttendanceReportTrendQuery(trendQuery);

  const { avgAttendancePerDay, maxAttendanceInDay } = useMemo(() => {
    if (trend.length === 0) {
      return { avgAttendancePerDay: 0, maxAttendanceInDay: 0 };
    }
    const total = trend.reduce((s, t) => s + t.totalAttendance, 0);
    const avg = Math.round(total / trend.length);
    const max = Math.max(...trend.map((t) => t.totalAttendance));
    return { avgAttendancePerDay: avg, maxAttendanceInDay: max };
  }, [trend]);

  const exportCsv = useCallback(() => {
    const header = "ວັນທີ,ຈຳນວນການເຂົ້າຮຽນ,ຈຳນວນນັກຮຽນທີ່ແຕກຕ່າງກັນ";
    const rows = trend.map(
      (t) =>
        `${escapeCsvCell(formatDate(t.date))},${escapeCsvCell(t.totalAttendance)},${escapeCsvCell(t.uniqueStudents)}`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [trend, dateFrom, dateTo]);

  const areaChartData: AreaChartData[] = useMemo(
    () =>
      trend.map((t) => ({
        date: formatDate(t.date),
        totalAttendance: t.totalAttendance,
        uniqueStudents: t.uniqueStudents,
      })),
    [trend],
  );

  const areaChartConfig: ChartConfig = {
    totalAttendance: {
      label: "ຈຳນວນການເຂົ້າຮຽນ",
      color: "var(--chart-1)",
    },
    uniqueStudents: {
      label: "ຈຳນວນນັກຮຽນ",
      color: "var(--chart-2)",
    },
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລາຍງານການເຂົ້າຮຽນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານການເຂົ້າຮຽນ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ສະແດງຈຳນວນການເຂົ້າຮຽນແລະນັກຮຽນທີ່ແຕກຕ່າງກັນ
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={trend.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <AttendanceReportFilter />
        </div>

        <ReportSummaryStatGrid columns={4}>
          <ReportSummaryStatCard
            title="ຈຳນວນການເຂົ້າຮຽນທັງໝົດ"
            icon={LogInIcon}
            tone="attendance"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.totalAttendance ?? 0)}
          />
          <ReportSummaryStatCard
            title="ຈຳນວນນັກຮຽນທີ່ແຕກຕ່າງກັນ"
            icon={GraduationCapIcon}
            tone="students"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.uniqueStudents ?? 0)}
          />
          <ReportSummaryStatCard
            title="ສະເລ່ຍການເຂົ້າຮຽນຕໍ່ວັນ"
            icon={BarChart3Icon}
            tone="packages"
            isLoading={trendLoading}
            value={reportSummaryCount(avgAttendancePerDay)}
          />
          <ReportSummaryStatCard
            title="ການເຂົ້າຮຽນສູງສຸດໃນວັນດຽວ"
            icon={TrendingUpIcon}
            tone="revenue"
            isLoading={trendLoading}
            value={reportSummaryCount(maxAttendanceInDay)}
          />
        </ReportSummaryStatGrid>

        {areaChartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>ແນວໂນ້ມການເຂົ້າຮຽນ</CardTitle>
              <p className="text-muted-foreground text-sm">ຕາມວັນທີໃນຊ່ວງທີ່ເລືອກ</p>
            </CardHeader>
            <CardContent>
              <AreaChart
                data={areaChartData}
                config={areaChartConfig}
                xDataKey="date"
                valueDataKey={["totalAttendance", "uniqueStudents"]}
                xAxisTickFormatter={(v) =>
                  typeof v === "string" ? v.slice(0, 10) : String(v)
                }
                className="mx-auto aspect-video max-h-[300px]"
              />
            </CardContent>
          </Card>
        )}

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>ຕາຕະລາງລາຍວັນ</CardTitle>
            <p className="text-muted-foreground text-sm">
              ຂໍ້ມູນການເຂົ້າຮຽນແຍກຕາມວັນທີ
            </p>
          </CardHeader>
          <CardContent>
            <AttendanceReportTable isLoading={trendLoading} data={trend} />
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
